import apiClient from '../../apis';
import type { ComposedAgentReply } from '../../apis';
import type { LadderSuggestion, PolicyBlock } from '../../apis';

export interface Usage { sessions: number; tokens_in: number; tokens_out: number; queries: number; cost_usd?: number }
export interface Costs {
  today: Usage; total: Usage; days: number; by_day: (Usage & { day: string })[];
  by_kind: Record<string, Usage>; can_view_money: boolean;
}

export async function getAgentCosts(agentId: string, days: number): Promise<Costs> {
  const { data } = await apiClient.get<Costs>(`/api/agents/${encodeURIComponent(agentId)}/costs`, { params: { days } });
  return data;
}

export interface SeatIdentity { slack: 'app' | 'member'; mailbox: 'agentmail' | 'workspace' | 'm365' }
export interface SeatMailbox { connection_id?: string; provider?: string; address: string; provisioned_at: string; deprovisioned_at?: string }
export interface SeatInvitation {
  id: string; kind: 'slack_channel' | 'drive_file' | 'notion_page';
  ref: string; from: string; received_at: string; status: string;
}

export interface Seat {
  identity?: SeatIdentity;
  identity_change?: import('../AgentStudio/api').AuditRow | null;
  slack_member?: { connection_id: string; member_user_id: string; display_name: string } | null;
  mailbox?: SeatMailbox | null;
  id: string;
  title: string;
  holder_user_id: string;
  holder_name: string;
  since: string;
  owner_id: string;
  org_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface ObjectiveFilter {
  dimension: string;
  operator: string;
  value?: unknown;
}

export interface Objective {
  metric: string;
  perspective_id: string | null;
  target: { kind: 'pct' | 'abs'; value: number };
  deadline: string | null;
  /** A fixed anchor (YYYY-MM-DD): the window ends here, not today — a demo snapshot. */
  as_of?: string | null;
  filters: ObjectiveFilter[];
  window_days: number;
  label: string | null;
  baseline: { value: number; window_start: string; window_end: string; measured_at: string } | null;
}

export interface Guardrail {
  metric: string;
  op: '<=' | '>=';
  bound: number;
}

export interface Cadence {
  /** `manual`: no schedule — the objective runs only when asked (the demo week's staged objectives, #583) */
  kind: 'scheduled' | 'manual';
  config: { daily_at?: string; weekday?: string; interval_minutes?: number };
}

export type AgentState = 'active' | 'paused' | 'retired';

export interface GuardrailResult extends Guardrail {
  value: number | null;
  breached: boolean | null;
  note: string | null;
}

export interface Progress {
  current: number | null;
  prior: number | null;
  delta: number | null;
  baseline: number | null;
  change_pct: number | null;
  target: Objective['target'];
  attainment: number | null;
  time_elapsed: number | null;
  on_pace: boolean | null;
  direction: string | null;
  /** Set under a fixed anchor: the window ended on this day and `baseline` is the window before it. */
  as_of?: string | null;
  baseline_kind?: 'prior_window' | null;
}

export type RunStatus = 'completed' | 'suppressed' | 'denied' | 'failed';

export interface AgentRunSummary {
  id: string;
  session_id: string | null;
  trigger: 'scheduled' | 'manual';
  status: RunStatus;
  kind: 'post' | 'observation' | 'system';
  tier: string | null;
  headline: string;
  target: Progress | Record<string, never>;
  guardrails: GuardrailResult[];
  next: LadderSuggestion[];
  next_note: string | null;
  suppressed_reason: string | null;
  policy: PolicyBlock;
  started_at: string;
  finished_at: string | null;
  error: string | null;
}

export interface AgentRun extends AgentRunSummary {
  why: Record<string, unknown> | null;
  summary: string;
  context: Record<string, unknown>;
}

export interface AgentRow {
  id: string;
  seat_id: string;
  name: string;
  state: AgentState;
  objective: Objective;
  guardrails: Guardrail[];
  harnesses: { agents: string[]; notes: string[] };
  envelope: { verbs: string[] };
  cadence: Cadence;
  reach: string;
  effort: 'quick' | 'deep';
  deployed: { by: string; by_name: string; at: string };
  last_run_at: string | null;
  last_run_status: string | null;
  last_run_id: string | null;
  owner_id: string;
  org_id: string | null;
  created_at: string;
  updated_at: string;
  seat: Seat | null;
  holds: boolean;
  last_run: AgentRunSummary | null;
  /** True only under the demo bypass until the Verifier (#203). */
  verified: boolean;
  delegate: { email: EmailGrant };
  /** The Verifier's stamp (#203): the track record Objectives show, never a gate. */
  verification?: { scored?: number; score?: number | null; bar?: number; last_scored_at?: string | null } | null;
  /** List rows only (#571): the objective's metric is in the VIEWER's catalog; null when that could not be read. */
  metric_visible?: boolean | null;
}

/** The allowlist a scoped email grant carries — the ONLY mail its reader ever opens (#205). */
export interface EmailScope { labels: string[]; senders: string[]; threads: string[] }

/**
 * The scoped, read-only, expiring email delegate (#205): honoured only for a
 * verified agent whose holder has opened it. `available` = the agent is
 * verified (so it CAN be opened); `enabled` = it is open and unexpired.
 */
export interface EmailGrant {
  /** Currently honoured — verified, toggled on, and unexpired. */
  enabled: boolean;
  /** The agent is verified, so a grant CAN be opened. */
  available: boolean;
  /** A grant is stored (toggle on), so the holder can always revoke it — even if not currently honoured. */
  revocable: boolean;
  reason: string | null;
  /** Owner-only: the allowlist, present only for the seat holder or an admin. */
  scope: EmailScope | null;
  objective_key: string | null;
  expires_at: string | null;
  /** Owner-only: the mailbox account the grant reads. */
  account?: string | null;
  /** The grant's toggle still reads on, but its window has passed. */
  expired: boolean;
  granted_by_name?: string;
  granted_at?: string;
  read_only?: true;
}

/** One thread the reader opened in a weekly digest — the seat holder's (or an admin's) view alone. */
export interface EmailReadThread {
  id: string;
  subject: string | null;
  from: string | null;
  at: string | null;
  claims: number;
}

/** One weekly email digest (#205): what matched the scope, what was skipped, the claims it left. */
export interface EmailDigest {
  id: string;
  objective_key: string | null;
  period_start: string | null;
  period_end: string | null;
  matched: number;
  skipped: number;
  claims: number;
  /** A safety cap was hit — not the whole window was read. */
  incomplete?: boolean;
  session_id: string | null;
  created_at: string;
  /** Present ONLY when the viewer is the mailbox owner of this digest or an org admin. */
  read_threads?: EmailReadThread[];
}

/** `GET /email`: the grant, its digests, and whether this viewer owns the mailbox view. */
export interface EmailView {
  grant: EmailGrant | null;
  digests: EmailDigest[];
  owner_view: boolean;
}

/** Open a scoped email grant (`enabled: true` + allowlist + expiry), or close it (`enabled: false`). */
export interface EmailDelegateBody {
  enabled: boolean;
  scope?: EmailScope;
  expires_at?: string;
  objective_key?: string;
}

/** One weekly scoring by the Verifier (#203). */
export interface VerificationAudit {
  id: string;
  window_start: string | null;
  window_end: string | null;
  scored: number;
  score: number | null;
  bar: number;
  verdict: 'pass' | 'fail' | 'insufficient';
  scores: Record<string, { value: number | null; n: number; hits?: number }>;
  verified_after: boolean;
  created_at: string;
}

export interface Verification {
  verified: boolean;
  scored: number;
  needed: number;
  progress: string;
  note: string;
  /** Verified by the demo bypass, not the Verifier (#203). */
  bypass?: boolean;
  score?: number | null;
  bar?: number;
  since?: string | null;
  revoked_at?: string | null;
  last_scored_at?: string | null;
  /** Newest first — the wall's audit grid. */
  audits?: VerificationAudit[];
}

export interface MemoryRow {
  id: string;
  agent_id: string;
  kind: 'steering' | 'context' | 'belief' | 'observation';
  key: string | null;
  content: string;
  tier: string;
  sources: unknown[];
  provenance: Record<string, unknown>;
  supersedes: string | null;
  superseded_by: string | null;
  expires_at: string | null;
  created_at: string;
}

export interface ActivityRow {
  id: string;
  agent_id: string;
  kind: string;
  summary: string;
  detail: Record<string, unknown>;
  provenance: Record<string, unknown>;
  created_at: string;
}

/** The agent wall (#202): the identity, its scoreboard, and its signed, append-only ledger. */
/** The briefing card (#298): what the last published briefing read, what changed, what it waits on, what this read dropped. */
export interface Briefing {
  claim_chips?: { id: string; speaker: string; check?: { verdict: 'match' | 'off' | 'unverifiable'; measured: number | null };
    hit_rate?: { matched: number; checked: number } }[];
  published_at: string | null;
  run_id?: string;
  read: { documents: number; claims: number; sources: string[]; blocks: { block: string; source: string; as_of: string }[] } | null;
  changed: { since: string | null; claims_added: number; claims_removed: number } | null;
  waiting_on: { kind: 'source' | 'promise' | 'person'; label: string; ref: string; promised_at?: string | null }[];
  dropped: { block: string; source: string; grant_id: string | null; reason: string }[];
  next_at: string | null;
}

export interface Wall {
  mailbox?: SeatMailbox | null;
  invitations?: SeatInvitation[];
  can_manage_invitations?: boolean;
  refresh?: { last_at: string | null; next_at: string | null; skipped: { at: string; reason: string } | null };
  agent: AgentRow;
  /** Present when the Unicorn switch is on (#298). */
  briefing?: Briefing | null;
  scoreboard: { proposals: number; executed: number; clean_runs: { n: number; of: number }; reach: { label: string; subscribers: number } };
  verification: Verification;
  delegate_grants: { email: EmailGrant };
  /** Newest first, already disclosed for the viewer. */
  activity: ActivityRow[];
  /** Kind `steering`, newest first (at most 20). */
  steering: MemoryRow[];
  subscribed: boolean;
  subscribers: number;
  pending_proposals: number;
}

/** What the agent says back in a chat turn: the text, and its status as of now. */
export interface ChatReply extends Partial<ComposedAgentReply> {
  text: string;
  status?: {
    objective_label: string;
    pace: string | null;
    attainment: number | null;
    on_pace: boolean | null;
    top_lever: string | null;
    pending_proposals: number;
    guardrails: GuardrailResult[];
    last_run_at: string | null;
    last_run_status: string | null;
    conflicts: string[];
  };
}

/** One turn of an agent chat, as kept in the thread's `snapshot.turns`. */
export interface AgentChatTurn extends Partial<ComposedAgentReply> {
  id: string;
  role: 'user' | 'agent';
  text: string;
  status?: ChatReply['status'];
  ts: string;
  /** A user turn's message id (`mintOpId`): a retry resends it, so the server writes it once. */
  op_id?: string;
  /** A user turn the agent never received — the retry affordance shows. */
  failed?: boolean;
}

/** What a chat or steer sends: the text, the thread it was said in, and the message id a retry reuses. */
export interface SteerBody { text: string; thread_id?: string; op_id?: string }

export interface AgentContext {
  items: unknown[];
  counts: Record<string, number>;
  chars: number;
  built_at: string;
}

export interface DeployRequest {
  name: string;
  seat_id?: string;
  seat_title?: string;
  objective: {
    metric: string;
    perspective_id?: string;
    target: { kind: 'pct' | 'abs'; value: number };
    deadline?: string;
    filters?: ObjectiveFilter[];
    window_days?: number;
    label?: string;
  };
  guardrails?: Guardrail[];
  cadence?: { daily_at?: string; weekday?: string; interval_minutes?: number };
  harnesses?: { agents?: string[]; notes?: string[] };
  effort?: 'quick' | 'deep';
}

const BASE = '/api/agents';

export async function getAgentKnowledge(id: string, by: import('../../apis').KnowledgeFacet = 'person', since?: string): Promise<import('../../apis').AgentKnowledgeDossier> {
  const { data } = await apiClient.get<import('../../apis').AgentKnowledgeDossier>(`${BASE}/${id}/knowledge`, { params: { by, since } });
  return data;
}

export async function listAgents(includeRetired = false): Promise<AgentRow[]> {
  const { data } = await apiClient.get<AgentRow[]>(`${BASE}/`, { params: includeRetired ? { include_retired: true } : undefined });
  return data;
}

export async function getAgent(id: string): Promise<AgentRow> {
  const { data } = await apiClient.get<AgentRow>(`${BASE}/${id}`);
  return data;
}

export async function deployAgent(body: DeployRequest): Promise<AgentRow> {
  const { data } = await apiClient.post<AgentRow>(`${BASE}/`, body);
  return data;
}

export async function pauseAgent(id: string): Promise<AgentRow> {
  const { data } = await apiClient.post<AgentRow>(`${BASE}/${id}/pause`);
  return data;
}

export async function resumeAgent(id: string): Promise<AgentRow> {
  const { data } = await apiClient.post<AgentRow>(`${BASE}/${id}/resume`);
  return data;
}

export async function retireAgent(id: string): Promise<AgentRow> {
  const { data } = await apiClient.delete<AgentRow>(`${BASE}/${id}`);
  return data;
}

/** The seat's holder edits the objective's guardrails (#571): a signed GUARDRAILS ledger row names before and after. */
export async function setAgentGuardrails(id: string, guardrails: Guardrail[]): Promise<AgentRow> {
  const { data } = await apiClient.put<AgentRow>(`${BASE}/${id}/guardrails`, { guardrails });
  return data;
}

export async function runAgent(id: string): Promise<AgentRun> {
  const { data } = await apiClient.post<AgentRun>(`${BASE}/${id}/run`, undefined, { timeout: 300000 });
  return data;
}

export async function listAgentRuns(id: string, limit = 20): Promise<AgentRunSummary[]> {
  const { data } = await apiClient.get<AgentRunSummary[]>(`${BASE}/${id}/runs`, { params: { limit } });
  return data;
}

export async function getAgentRun(id: string, runId: string): Promise<AgentRun> {
  const { data } = await apiClient.get<AgentRun>(`${BASE}/${id}/runs/${runId}`);
  return data;
}

export async function listAgentMemory(id: string, params?: { kind?: string; include_superseded?: boolean }): Promise<MemoryRow[]> {
  const { data } = await apiClient.get<MemoryRow[]>(`${BASE}/${id}/memory`, { params });
  return data;
}

export async function listAgentActivity(id: string): Promise<ActivityRow[]> {
  const { data } = await apiClient.get<ActivityRow[]>(`${BASE}/${id}/activity`);
  return data;
}

export async function getAgentContext(id: string): Promise<AgentContext> {
  const { data } = await apiClient.get<AgentContext>(`${BASE}/${id}/context`);
  return data;
}

export async function listSeats(): Promise<Seat[]> {
  const { data } = await apiClient.get<Seat[]>(`${BASE}/seats`);
  return data;
}

export async function createSeat(title: string): Promise<Seat> {
  const { data } = await apiClient.post<Seat>(`${BASE}/seats`, { title });
  return data;
}

export async function setSeatHolder(id: string, holderUserId: string): Promise<Seat> {
  const { data } = await apiClient.put<Seat>(`${BASE}/seats/${id}/holder`, { holder_user_id: holderUserId });
  return data;
}

export async function getAgentWall(id: string): Promise<Wall> {
  const { data } = await apiClient.get<Wall>(`${BASE}/${id}/wall`);
  return data;
}

export async function steerAgent(id: string, body: SteerBody): Promise<{ steering: MemoryRow; activity: ActivityRow }> {
  const { data } = await apiClient.post<{ steering: MemoryRow; activity: ActivityRow }>(`${BASE}/${id}/steer`, body);
  return data;
}

export async function chatWithAgent(id: string, body: SteerBody): Promise<{ reply: ChatReply; steering?: MemoryRow; activity?: ActivityRow; promise?: PromiseRow; session_id?: string }> {
  const { data } = await apiClient.post<{ reply: ChatReply; steering?: MemoryRow; activity?: ActivityRow; promise?: PromiseRow; session_id?: string }>(`${BASE}/${id}/chat`, body, { timeout: 120000 });
  return data;
}

export async function getAgentChatOpening(id: string): Promise<{ reply: ChatReply }> {
  const { data } = await apiClient.get<{ reply: ChatReply }>(`${BASE}/${id}/chat/opening`);
  return data;
}

/** The refresh schedule (#298): the agent's cadence, set by the seat's holder; the runner picks it up on its next tick. */
export async function setAgentRefresh(id: string, cadence: { daily_at?: string; interval_minutes?: number; weekday?: string }): Promise<{ cadence: any; next_at: string }> {
  const { data } = await apiClient.put<{ data: { cadence: any; next_at: string } }>(`${BASE}/${id}/refresh`, { cadence });
  return (data as any).data ?? data;
}


export async function subscribeAgent(id: string): Promise<{ subscribed: boolean; subscribers: number }> {
  const { data } = await apiClient.post<{ subscribed: boolean; subscribers: number }>(`${BASE}/${id}/subscribe`);
  return data;
}

export async function unsubscribeAgent(id: string): Promise<{ subscribed: boolean; subscribers: number }> {
  const { data } = await apiClient.delete<{ subscribed: boolean; subscribers: number }>(`${BASE}/${id}/subscribe`);
  return data;
}

/** Ask the Verifier (#203) to score this agent now — holder or admin. */
export async function verifyAgentNow(id: string): Promise<{ audit: VerificationAudit; verification: Verification }> {
  const { data } = await apiClient.post<{ audit: VerificationAudit; verification: Verification }>(`${BASE}/${id}/verify`);
  return data;
}

export async function getVerification(id: string): Promise<Verification> {
  const { data } = await apiClient.get<Verification>(`${BASE}/${id}/verification`);
  return data;
}

/** The holder opens or closes the scoped email grant (#205); 400 without an allowlist/expiry, 409 when unverified. */
export async function setEmailDelegate(id: string, body: EmailDelegateBody): Promise<AgentRow> {
  const { data } = await apiClient.put<AgentRow>(`${BASE}/${id}/delegate/email`, body);
  return data;
}

/** The email grant and its weekly digests; `read_threads` come back only for the seat holder or an admin. */
export async function getAgentEmail(id: string, limit = 26): Promise<EmailView> {
  const { data } = await apiClient.get<EmailView>(`${BASE}/${id}/email`, { params: { limit } });
  return data;
}

/** Read the scoped mailbox now (holder or admin); 409 when the grant is not active or the agent is busy. */
export async function readAgentEmailNow(id: string): Promise<EmailDigest> {
  const { data } = await apiClient.post<EmailDigest>(`${BASE}/${id}/email/read`, undefined, { timeout: 120000 });
  return data;
}

/** The server's error string for a failed call, else the transport's. */
export function agentErrorMessage(e: any, fallback = 'Request failed'): string {
  return e?.response?.data?.error || e?.message || fallback;
}

export type PromiseRow = import('../../types/knowledge').WorkItem & {
  session_id: string;
  asked_by_name?: string | null;
  meeting_title?: string | null;
  text?: string;
};
export interface PromiseQueue { to_me: PromiseRow[]; by_my_agents: PromiseRow[] }

export async function getPromises(agentId?: string): Promise<PromiseQueue> {
  if (agentId) {
    const { data } = await apiClient.get<PromiseRow[]>(`${BASE}/${agentId}/promises`);
    return { to_me: data, by_my_agents: [] };
  }
  const { data } = await apiClient.get<PromiseQueue>('/api/me/promises');
  return data;
}


export async function decideInvitation(agentId: string, invitationId: string, action: 'accept' | 'decline'): Promise<void> {
  await apiClient.post(`/api/agents/${encodeURIComponent(agentId)}/invitations/${encodeURIComponent(invitationId)}/${action}`);
}

// ── Groups (#307): a named set of seats with an address; a router, never a brain ──
export interface GroupMember {
  seat_id: string; seat_title: string | null; holder_name: string | null;
  agent_id: string; agent_name: string; objective_label: string;
}
export interface GroupRow {
  id: string; name: string; kind: 'domain' | 'preference'; address: string; perspective_id: string | null;
  member_seats: string[]; members: GroupMember[]; created_at: string; updated_at: string; session_id?: string | null;
}
export interface GroupCreate { name: string; kind: 'domain' | 'preference'; perspective_id?: string; members?: string[]; address?: string }
export interface GroupRollupLine { agent_id: string; agent_name: string; text: string }
export interface GroupScore { agent_id: string; agent_name: string; score: number }
export type GroupAnswer =
  | { kind: 'answer'; reply: ChatReply; spoken_as: { agent_id: string; agent_name: string; seat_id: string | null }; scores: GroupScore[]; session_id: string; promise?: PromiseRow }
  | { kind: 'rollup'; lines: GroupRollupLine[]; spoken_as: null; scores: GroupScore[]; session_id: string };
/** A turn in a group chat: a user line, or the group's reply — spoken by one member or a roll-up. */
export interface GroupChatTurn {
  id: string; role: 'user' | 'group'; text: string; ts: string; op_id?: string; failed?: boolean;
  spoken_as?: { agent_id: string; agent_name: string } | null; lines?: GroupRollupLine[]; reply?: ChatReply; session_id?: string;
  /** The promise a fresh-data question made on the binding member (#300): its delivery lands in this thread. */
  promise?: PromiseRow | null;
}

export async function listGroups(): Promise<GroupRow[]> {
  const { data } = await apiClient.get<GroupRow[]>(`${BASE}/groups`);
  return data;
}
export async function createGroup(body: GroupCreate): Promise<GroupRow> {
  const { data } = await apiClient.post<GroupRow>(`${BASE}/groups`, body);
  return data;
}
export async function disbandGroup(id: string): Promise<void> {
  await apiClient.delete(`${BASE}/groups/${encodeURIComponent(id)}`);
}
export async function groupChat(id: string, body: { text: string; thread_id?: string | null; op_id?: string }): Promise<GroupAnswer> {
  const { data } = await apiClient.post<GroupAnswer>(`${BASE}/groups/${encodeURIComponent(id)}/chat`, body);
  return data;
}
